import React, { useCallback, useState } from 'react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router'
import { useMedia } from 'react-use'
import styled from 'styled-components'
import List from '~/components/list'
import AddIcon from '~/components/icons/ui/24px_bold-add.svg'
import EditIcon from '~/components/icons/ui/24px_edit-72.svg'
import RemoveIcon from '~/components/icons/ui/24px_bold-remove.svg'
import { toastError, toastSuccess } from '~/components/toasts'
import { colors } from '~/design'
import { parseDateTime } from '~/prix/types/dateTime'
import useAction from '~/prix/react/hooks/action'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import emergencyDispatchDeleteDefinition from './delete/emergencyDispatchDelete.definition'
import emergencyDispatchListDefinition from './list/emergencyDispatchList.definition'

const TransparentButton = styled.button`
  display: flex;
  flex: 1;
  background-color: transparent;
  border: none;
  text-align: center;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &[disabled] {
    opacity: 0.6;
    background-color: transparent;
    border: none;
  }
`

const Wrapper = styled.div`
  background-color: #f5f5f5;
  flex-direction: column;

  @media (max-width: 870px) {
    padding-top: 30px;
  }

  .action-buttons {
    display: flex;
    align-items: center;
    gap: 5px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${colors.prixBlue};
      border-radius: 5px;
      padding: 6px;
      border: none;
      cursor: pointer;
    }
  }
`

type EmergencyDispatchListItem = {
  id: string
  code: number
  title: string
  status: string
  fullAddress: string
  createdAt: string
}

export default function EmergencyDispatchListScreen() {
  const navigate = useNavigate()
  const firstBreak = useMedia('(max-width: 1230px)')
  const secondBreak = useMedia('(max-width: 1060px)')
  const thirdBreak = useMedia('(max-width: 540px)')
  const [reloadKey, setReloadKey] = useState(0)

  const { callAction: callDeleteEmergencyDispatch } = useActionFunction(
    emergencyDispatchDeleteDefinition,
    { initialIsLoading: false },
  )

  const emergencyDispatchList = useAction(
    emergencyDispatchListDefinition,
    {
      limit: 5000,
      orderBy: 'createdAt',
      order: 'descending',
    },
    [reloadKey],
    {
      autoLoad: true,
      staleWhileRevalidate: false,
    },
  )

  const deleteEmergencyDispatch = useCallback(
    (emergencyDispatchIdentifier: string) => {
      const confirmed = window.confirm('Confirme a remoção do atendimento')
      if (!confirmed) {
        return
      }

      const { resultPromise } = callDeleteEmergencyDispatch({
        id: emergencyDispatchIdentifier,
        updatedAt: new Date().toISOString(),
      })

      resultPromise
        .then(() => {
          toastSuccess('Atendimento removido com sucesso!')
          setReloadKey(currentReloadKey => currentReloadKey + 1)
        })
        .catch(error => {
          toastError(error.message || 'Erro ao remover atendimento')
        })
    },
    [callDeleteEmergencyDispatch],
  )

  const items: EmergencyDispatchListItem[] =
    emergencyDispatchList.result?.items?.map(item => ({
      id: item.id,
      code: item.code,
      title: item.title,
      status: item.status,
      fullAddress: item.fullAddress,
      createdAt: item.createdAt ? format(parseDateTime(item.createdAt), 'dd/LL/yyyy') : '',
    })) ?? []

  return (
    <Wrapper>
      <List
        title='Lista de atendimentos'
        items={items}
        isLoading={emergencyDispatchList.isLoading}
        itemType='atendimentos'
        extraButtons={
          <TransparentButton onClick={() => navigate('../attendances/create')} title='Cadastrar'>
            <AddIcon width={19} height={19} fill='#fff' />
          </TransparentButton>
        }
        fractions={
          firstBreak && !secondBreak
            ? {
                code: 0.5 / 4,
                title: 1.25 / 4,
                status: 0.75 / 4,
                action: 0.5 / 4,
              }
            : secondBreak && !thirdBreak
              ? {
                  title: 1.5 / 3,
                  status: 1 / 3,
                  action: 0.5 / 3,
                }
              : thirdBreak
                ? {
                    title: 1.25 / 3,
                    status: 1 / 3,
                    action: 0.75 / 3,
                  }
                : {
                    code: 0.5 / 5,
                    title: 1.25 / 5,
                    status: 0.75 / 5,
                    fullAddress: 1.75 / 5,
                    action: 0.75 / 5,
                  }
        }
        labels={
          firstBreak && !secondBreak
            ? {
                code: 'Código',
                title: 'Título',
                status: 'Status',
                action: 'Ações',
              }
            : secondBreak
              ? {
                  title: 'Título',
                  status: 'Status',
                  action: 'Ações',
                }
              : {
                  code: 'Código',
                  title: 'Título',
                  status: 'Status',
                  fullAddress: 'Endereço',
                  action: 'Ações',
                }
        }
        actions={(item: EmergencyDispatchListItem) => (
          <div className='action-buttons'>
            <button title='Editar' onClick={() => navigate(`../attendances/update/${item.id}`)}>
              <EditIcon width={14} height={14} fill='#fff' />
            </button>
            <button title='Remover' onClick={() => deleteEmergencyDispatch(item.id)}>
              <RemoveIcon width={14} height={14} fill='#fff' />
            </button>
          </div>
        )}
      />
    </Wrapper>
  )
}
